import React from "react";
import { cn } from "@/lib/utils";
import Container from "./Container";
import FadeIn from "./FadeIn";

/**
 * Section — vertical rhythm block with optional eyebrow, headline, subheadline.
 *
 * Props:
 *   eyebrow, headline, subheadline (optional strings/nodes)
 *   align: "left" | "center"
 *   spacing: "sm" | "md" | "lg" (default "lg")
 *   contained: boolean (wrap children in Container; default true)
 */
export const Section = React.forwardRef(function Section(
  {
    id,
    eyebrow,
    headline,
    subheadline,
    align = "left",
    spacing = "lg",
    contained = true,
    className,
    headerClassName,
    children,
    ...props
  },
  ref,
) {
  const spacingMap = {
    sm: "py-16 sm:py-20",
    md: "py-20 sm:py-24 lg:py-28",
    lg: "py-24 sm:py-28 lg:py-32",
  };

  const alignCls = align === "center" ? "items-center text-center" : "items-start text-left";

  const header = (eyebrow || headline || subheadline) && (
    <div
      className={cn(
        "flex flex-col gap-4 max-w-3xl",
        align === "center" && "mx-auto",
        headerClassName,
      )}
    >
      {eyebrow && (
        <FadeIn>
          <span
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-emerald"
            data-testid="section-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-[0_0_10px_rgba(0,230,118,0.8)]" />
            {eyebrow}
          </span>
        </FadeIn>
      )}
      {headline && (
        <FadeIn delay={0.05}>
          <h2
            className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl text-balance"
            data-testid="section-headline"
          >
            {headline}
          </h2>
        </FadeIn>
      )}
      {subheadline && (
        <FadeIn delay={0.1}>
          <p
            className="text-base text-text-secondary sm:text-lg text-pretty max-w-2xl"
            data-testid="section-subheadline"
          >
            {subheadline}
          </p>
        </FadeIn>
      )}
    </div>
  );

  const inner = (
    <div className={cn("flex flex-col gap-14", alignCls)}>
      {header}
      {children && <div className="w-full">{children}</div>}
    </div>
  );

  return (
    <section
      ref={ref}
      id={id}
      className={cn("relative", spacingMap[spacing], className)}
      {...props}
    >
      {contained ? <Container>{inner}</Container> : inner}
    </section>
  );
});

export default Section;
